import React, {useCallback, useEffect, useMemo, useState} from "react";
import styled from "styled-components";
import {useAtom} from "jotai";
import {useNavigate} from "react-router-dom";
import {Board, BoardHeader, BoardSearchResult, RowSpan} from "../../assets/GlobalStyles";
import Table, {Icon} from "../../components/table";
import SearchBoard from "../../components/common/SearchBoard";
import HoverCampaigns from "../../components/common/HoverCampaigns";
import {convertInventoryExamination, convertInventoryPublishYn, selInventoryList} from "../../services/mediamanage/InventoryAxios";
import {updateAllowCampaignWeight} from "../../services/reports/mediaAxios";
import {columnData, mediaSearchResult} from "./entity/medialist";
import {deviceTypeInfo, productTypeInfo, searchInfo} from "./entity/common";
import {confirmAllType} from "./entity/medialistdetail";
import {Off, On, SwitchBox} from "../../components/table/styles/common";
import SelectBox from "../../components/common/SelectBox";

function MediaList() {
  const [inventorySearchList, setInventorySearchList] = useAtom(mediaSearchResult);
  const [allInventoryList, setAllInventoryList] = useState([]);
  const [mediaFilterResults, setMediaFilterResults] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    selInventoryList(searchInfo).then(response => {
      if (response) {
        setInventorySearchList(response);
        setAllInventoryList(response);
        if (response.length > 0) {
          setSelectedMedia(response[0].username || response[0].siteName);
        }
      }
    });
  }, [setInventorySearchList]);

  const mediaList = useMemo(() => {
    const groupedMedia = new Map();

    const mediaSource = mediaFilterResults ?? allInventoryList;

    mediaSource.forEach(item => {
      const key = item.username || item.siteName;
      if (!key) return;

      if (!groupedMedia.has(key)) {
        groupedMedia.set(key, {
          key,
          siteName: item.siteName || "-",
          username: item.username || "-",
          count: 0
        });
      }
      groupedMedia.get(key).count += 1;
    });

    return Array.from(groupedMedia.values());
  }, [allInventoryList, mediaFilterResults]);

  const displayedInventoryList = useMemo(() => {
    if (mediaFilterResults !== null) {
      if (!selectedMedia) return [];
      return mediaFilterResults.filter(item =>
          (item.username || item.siteName) === selectedMedia
      );
    }

    if (!selectedMedia) return inventorySearchList;
    return inventorySearchList.filter(item =>
        (item.username || item.siteName) === selectedMedia
    );
  }, [inventorySearchList, mediaFilterResults, selectedMedia]);

  useEffect(() => {
    setSelectedInventory(current => {
      const currentId = current?.inventoryId;
      return displayedInventoryList.find(item => item.inventoryId === currentId)
          || displayedInventoryList[0]
          || null;
    });
  }, [displayedInventoryList]);

  useEffect(() => {
    if (!selectedInventory) return;
    setDetailLoading(true);
    const timer = setTimeout(() => setDetailLoading(false), 400);
    return () => clearTimeout(timer);
  }, [selectedInventory?.inventoryId]);

  const onClickSearchMedia = condition => {
    const searchType = condition.searchKeywordType?.value;
    const keyword = condition.keyword?.trim() || "";

    if (searchType === "SITE_NAME" && keyword) {
      const normalizedKeyword = keyword.toLocaleLowerCase("ko-KR");
      const filteredMedia = allInventoryList.filter(item =>
          (item.siteName || "").toLocaleLowerCase("ko-KR").includes(normalizedKeyword)
      );

      setMediaFilterResults(filteredMedia);
      setInventorySearchList([]);
      setSelectedMedia(null);
      setSelectedInventory(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setMediaFilterResults(null);
    if (condition.searchKeywordType?.value === "USERNAME" && condition.keyword) {
      setSelectedMedia(condition.keyword);
    } else {
      setSelectedMedia(null);
    }
    selInventoryList(condition)
    .then(response => {
      if (response) {
        setInventorySearchList(response);
      }
    })
    .finally(() => setLoading(false));
  };

  const handlePublishChange = useCallback(async (inventoryId, nextValue) => {
    const updated = await convertInventoryPublishYn(inventoryId, nextValue);
    if (!updated) return false;

    const publishYn = nextValue ? "Y" : "N";
    const updateRow = item => item.inventoryId === inventoryId
        ? {...item, publishYn}
        : item;

    setInventorySearchList(current => current.map(updateRow));
    setAllInventoryList(current => current.map(updateRow));
    setMediaFilterResults(current => current === null ? null : current.map(updateRow));
    setSelectedInventory(current => current?.inventoryId === inventoryId
        ? {...current, publishYn}
        : current);
    return true;
  }, [setInventorySearchList]);

  return (
      <InventoryListPage>
        <Board>
          <BoardHeader>
            지면 리스트 관리
          </BoardHeader>
          <BoardSearchResult>
            <SettingCard $compact>
              <CompactSearchHeader>
                <SectionTitle>지면 검색</SectionTitle>
              </CompactSearchHeader>
              <FilterBox>
                <SearchBoard searchKeyword onSearch={onClickSearchMedia}/>
              </FilterBox>
            </SettingCard>
            <StatusSectionHeader>
              <SectionTitle>지면 현황</SectionTitle>
              <SectionGuide>검색 조건에 해당하는 지면 목록과 운영 상태를 확인합니다.</SectionGuide>
            </StatusSectionHeader>
            <SettingCard>
              <StatusBodyCard>
                <StatusLayout>
                <MediaListPanel>
                  <PanelHeader>
                    <div>
                      <PanelTitle>매체 목록</PanelTitle>
                      <PanelGuide>매체를 선택해 지면을 확인하세요.</PanelGuide>
                    </div>
                    <PanelCount>{mediaList.length}개</PanelCount>
                  </PanelHeader>
                  <MediaTable>
                    <MediaTableHeader>
                      <span>매체</span>
                      <span>지면</span>
                    </MediaTableHeader>
                    <MediaListBody>
                      {mediaList.map(media => (
                          <MediaListItem
                              type="button"
                              key={media.key}
                              $active={selectedMedia === media.key}
                              onClick={() => setSelectedMedia(media.key)}
                          >
                            <MediaIdentity>
                              <strong>{media.siteName}</strong>
                              <span>{media.username}</span>
                            </MediaIdentity>
                            <MediaItemMeta>
                              <InventoryCount>{media.count}</InventoryCount>
                            </MediaItemMeta>
                          </MediaListItem>
                      ))}
                      {mediaList.length === 0 && (
                          <EmptyMedia>조회된 매체가 없습니다.</EmptyMedia>
                      )}
                    </MediaListBody>
                  </MediaTable>
                </MediaListPanel>
                <InventoryPanel>
                  <PanelHeader>
                    <InventoryHeaderMain>
                      <PanelTitleRow>
                        <PanelTitle>지면 목록</PanelTitle>
                        <PanelCount>{displayedInventoryList.length}개 지면</PanelCount>
                      </PanelTitleRow>
                      <PanelGuide>선택한 매체의 지면과 운영 상태를 확인하세요.</PanelGuide>
                    </InventoryHeaderMain>
                    <SelectedMediaBadge>
                      <span>선택 매체</span>
                      <strong>
                        {selectedMedia
                            ? mediaList.find(media => media.key === selectedMedia)?.siteName || "선택한 매체"
                            : "전체 매체"}
                      </strong>
                    </SelectedMediaBadge>
                  </PanelHeader>
                {loading && (
                    <LoadingOverlay>
                      <LoadingSpinner/>
                      <span>지면 정보를 불러오고 있습니다.</span>
                    </LoadingOverlay>
                )}
                <Table
                    columns={columnData(handlePublishChange)}
                    idProperty="inventoryId"
                    emptyText="지면 리스트가 없습니다."
                    activeCell={null}
                    defaultSortInfo={{name: "createdAt", dir: -1}}
                    headerHeight={44}
                    rowHeight={50}
                    style={{height: 516, minHeight: 516}}
                    data={displayedInventoryList}
                    onRowClick={({data}) => setSelectedInventory(data)}
                    rowClassName={({data}) =>
                        data.inventoryId === selectedInventory?.inventoryId
                            ? "inventory-row-selected"
                            : ""
                    }
                />
                </InventoryPanel>
                <InventoryDetailCard>
                  {detailLoading ? (
                      <DetailLoading>
                        <LoadingSpinner/>
                        <span>지면 정보를 불러오고 있습니다.</span>
                      </DetailLoading>
                  ) : selectedInventory ? (
                      <>
                        <DetailHead>
                          <div>
                            <SelectedInventoryLabel>선택 지면</SelectedInventoryLabel>
                            <DetailTitle title={selectedInventory.inventoryName}>
                              {selectedInventory.inventoryName}
                            </DetailTitle>
                          </div>
                          <PublishBadge $active={selectedInventory.publishYn === "Y"}>
                            {selectedInventory.publishYn === "Y" ? "게재 중" : "게재 중지"}
                          </PublishBadge>
                        </DetailHead>

                        <DetailSection>
                          <DetailSectionTitle>기본 정보</DetailSectionTitle>
                          <DetailRow><span>매체</span><strong>{selectedInventory.siteName || "-"}</strong></DetailRow>
                          <DetailRow>
                            <span>지면 코드</span>
                            <DetailActionValue>
                              {selectedInventory.inventoryId && (
                                  <Icon icon="copyCode" value={selectedInventory.inventoryId}/>
                              )}
                            </DetailActionValue>
                          </DetailRow>
                          <DetailRow>
                            <span>광고 상품</span>
                            <strong>{productTypeInfo.find(item => item.value === selectedInventory.productType)?.label || selectedInventory.productType || "-"}</strong>
                          </DetailRow>
                          <DetailRow>
                            <span>디바이스</span>
                            <strong>{deviceTypeInfo.find(item => item.value === selectedInventory.deviceType)?.label || selectedInventory.deviceType || "-"}</strong>
                          </DetailRow>
                          <CampaignDetailRow>
                            <CampaignDetailLabel><i/>연결 캠페인</CampaignDetailLabel>
                            <DetailActionValue>
                              <HoverCampaigns
                                  inventoryId={selectedInventory.inventoryId}
                                  inventoryName={selectedInventory.inventoryName}
                                  ids={selectedInventory.allowCampaigns}
                                  onSave={async draft => {
                                    const updated = await updateAllowCampaignWeight(draft);
                                    if (updated) {
                                      setSelectedInventory(current => ({
                                        ...current,
                                        allowCampaigns: draft
                                      }));
                                      setInventorySearchList(current =>
                                          current.map(item =>
                                              item.inventoryId === selectedInventory.inventoryId
                                                  ? {...item, allowCampaigns: draft}
                                                  : item
                                          )
                                      );
                                    }
                                  }}
                              />
                            </DetailActionValue>
                          </CampaignDetailRow>
                        </DetailSection>

                        <DetailSection>
                          <DetailSectionTitle>운영 정보</DetailSectionTitle>
                          <DetailRow>
                            <span>심사 상태</span>
                            <DetailActionValue>
                              <SelectBox
                                  options={confirmAllType}
                                  value={selectedInventory.examinationStatus}
                                  onSelect={async item => {
                                    const updated = await convertInventoryExamination(selectedInventory.inventoryId, item);
                                    if (updated) {
                                      setSelectedInventory(current => ({...current, examinationStatus: item}));
                                      setInventorySearchList(current =>
                                          current.map(row =>
                                              row.inventoryId === selectedInventory.inventoryId
                                                  ? {...row, examinationStatus: item}
                                                  : row
                                          )
                                      );
                                    }
                                  }}
                              />
                            </DetailActionValue>
                          </DetailRow>
                          <DetailRow><span>지면 유형</span><strong>{selectedInventory.inventoryType || "-"}</strong></DetailRow>
                          <DetailRow>
                            <span>사이트</span>
                            {(selectedInventory.siteUrl || selectedInventory.mediaUrl) ? (
                                <SiteLink
                                    href={selectedInventory.siteUrl || selectedInventory.mediaUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={selectedInventory.siteUrl || selectedInventory.mediaUrl}
                                >
                                  사이트 열기
                                </SiteLink>
                            ) : <strong>-</strong>}
                          </DetailRow>
                        </DetailSection>

                        <DetailSection>
                          <DetailSectionTitle>정산·계약</DetailSectionTitle>
                          <DetailRow><span>정산 유형</span><strong>{selectedInventory.feeCalculation?.calculationType || "-"}</strong></DetailRow>
                          <DetailRow>
                            <span>정산 금액</span>
                            <strong>
                              {selectedInventory.feeCalculation?.calculationValue != null
                                  ? Number(selectedInventory.feeCalculation.calculationValue).toLocaleString("ko-KR")
                                  : "-"}
                            </strong>
                          </DetailRow>
                          <DetailRow>
                            <span>계약 시작일</span>
                            <strong>{formatContractDate(selectedInventory.feeCalculation?.contractStartDate)}</strong>
                          </DetailRow>
                        </DetailSection>

                        <DetailButton
                            type="button"
                            onClick={() => navigate("/board/mediaListDetail", {state: selectedInventory.inventoryId})}
                        >
                          전체 정보 보기
                        </DetailButton>
                      </>
                  ) : (
                      <DetailEmpty>
                        <strong>지면을 선택해 주세요.</strong>
                        <span>선택한 지면의 기본 정보가 표시됩니다.</span>
                      </DetailEmpty>
                  )}
                </InventoryDetailCard>
                </StatusLayout>
              </StatusBodyCard>
            </SettingCard>
          </BoardSearchResult>
        </Board>
      </InventoryListPage>
  );
}

const formatContractDate = value => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return new Intl.DateTimeFormat("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(date);
};

export default MediaList;

const InventoryListPage = styled.div`
  ${Board} {
    width: 100%;
    min-width: 0;
    margin: 0;
    padding: 20px;
    overflow: hidden;
    border: 1px solid #e5e9ee;
    border-radius: 20px;
    box-shadow: 0 10px 34px rgba(31, 41, 55, .09);
  }

  ${BoardHeader} {
    min-height: 36px;
    justify-content: flex-start;
    padding: 0 0 13px;
    border-bottom: 1px solid #e5e9ee;
    background: var(--color-surface);
    color: #202a35;
    font-size: var(--font-size-17);
    font-weight: 700;
  }

  ${BoardSearchResult} {
    margin: 0;
    padding: 20px 0 0;
    background: var(--color-surface);
  }

  .InovuaReactDataGrid--theme-default-light {
    overflow: hidden;
    border: 1px solid #e4e9ee;
    border-bottom: 1px solid #d9e1e9 !important;
    border-radius: 8px;
    box-sizing: border-box;
    background: var(--color-surface);
    color: #414b57;
    font-size: var(--font-size-13);
  }

  .InovuaReactDataGrid__header,
  .InovuaReactDataGrid__column-header {
    border-color: #cddff5 !important;
    background: #f2f7fd;
  }

  .InovuaReactDataGrid__header {
    border-bottom: 2px solid #8bb8ea !important;
  }

  .InovuaReactDataGrid__column-header {
    color: #34475a;
    font-size: var(--font-size-13);
    font-weight: 750;
  }

  .InovuaReactDataGrid__row {
    border-color: #edf1f4 !important;
    background: var(--color-surface);
    color: var(--color-text);
    font-size: var(--font-size-13);
    transition: background .15s ease, box-shadow .15s ease;
  }

  .InovuaReactDataGrid__row:hover {
    background: #f7fbff !important;
    box-shadow: inset 3px 0 0 var(--color-primary);
  }

  .InovuaReactDataGrid__cell {
    border-color: #edf1f4 !important;
    padding-right: 12px;
    padding-left: 12px;
    font-size: var(--font-size-13);
  }

  .InovuaReactDataGrid__cell a {
    color: var(--color-primary-strong) !important;
    font-size: var(--font-size-13);
    font-weight: 700;
    text-decoration: none;
  }

  .InovuaReactDataGrid__cell a:hover {
    color: var(--color-primary-dark) !important;
    text-decoration: underline;
  }

  @media (max-width: 720px) {
    ${BoardHeader} {
      min-height: 34px;
      padding: 0 0 12px;
    }

    ${BoardSearchResult} {
      padding: 16px 0 0;
    }
  }
`;

const SettingCard = styled.section`
  margin-bottom: ${({$compact}) => ($compact ? "18px" : "22px")};

  &:last-child {
    margin-bottom: 0;
  }
`;

const CompactSearchHeader = styled.div`
  margin-bottom: 8px;
`;

const SectionTitle = styled.div`
  color: #222;
  font-size: var(--font-size-17);
  font-weight: 700;
`;

const SectionGuide = styled.div`
  margin-top: 5px;
  color: #888;
  font-size: var(--font-size-13);
`;

const StatusSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin: 2px 0 12px;

  ${SectionGuide} {
    margin-top: 0;
    text-align: right;
  }

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;

    ${SectionGuide} {
      text-align: left;
    }
  }
`;

const StatusBodyCard = styled.div`
  padding: 16px;
  border: 1px solid #e1e6eb;
  border-radius: 12px;
  background: var(--color-surface);
  box-shadow: 0 5px 18px rgba(31, 41, 55, .05);

  @media (max-width: 720px) {
    padding: 12px;
  }
`;

const FilterBox = styled.div`
  padding: 10px;
  border: 1px solid #e1e6eb;
  border-radius: 8px;
  background: #f7f8fa;

  ${RowSpan} {
    min-height: 46px;
    margin: 0;
    flex-wrap: wrap;
  }

  [class*="-control"] {
    min-height: 38px !important;
    border-color: var(--color-border-strong) !important;
    border-radius: 7px !important;
    box-shadow: none !important;
  }

  [class*="-control"]:focus-within {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, .1) !important;
  }

  input[type='text'] {
    height: 38px !important;
    border-color: var(--color-border-strong) !important;
    font-size: var(--font-size-13);
    outline: none;
  }

  .search-keyword-group {
    width: min(560px, calc(100% - 80px));
    align-items: stretch;
  }

  .search-keyword-group > div:first-child {
    width: 145px;
    height: 38px;
    flex: 0 0 145px;
    z-index: 2;
  }

  .search-keyword-group [class*="-control"] {
    height: 38px !important;
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  .search-keyword-group > div:last-child input[type='text'] {
    padding: 0 14px;
    border-left: 0 !important;
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    border-top-right-radius: 7px !important;
    border-bottom-right-radius: 7px !important;
  }

  .search-keyword-group:focus-within {
    border-radius: 7px;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, .1);
  }

  .search-keyword-group:focus-within [class*="-control"],
  .search-keyword-group:focus-within input[type='text'] {
    border-color: var(--color-primary) !important;
  }

  @media (max-width: 640px) {
    .search-keyword-group {
      width: 100%;
    }

    .search-keyword-group > div:first-child {
      width: 120px;
      flex-basis: 120px;
    }
  }

  input[type='text']:focus {
    border-color: var(--color-primary) !important;
  }

  button {
    height: 38px;
    border-color: var(--color-primary);
    border-radius: 7px;
    background: var(--color-primary-soft);
    color: var(--color-primary-strong);
    font-size: var(--font-size-13);
    font-weight: 700;
  }
`;

const StatusLayout = styled.div`
  display: grid;
  grid-template-columns: 250px minmax(560px, 1fr) 290px;
  gap: 16px;
  min-width: 0;

  @media (max-width: 1320px) {
    grid-template-columns: 230px minmax(520px, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const MediaListPanel = styled.section`
  min-width: 0;
  height: 584px;
  overflow: hidden;
  border: 0;
  border-radius: 0;
  background: var(--color-surface);

  @media (max-width: 720px) {
    height: auto;
  }
`;

const InventoryPanel = styled.section`
  position: relative;
  min-width: 0;
  height: 584px;
  overflow: hidden;

  ${SwitchBox} {
    width: 48px;
    height: 22px;
    border-radius: 22px;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, .18);

    > label {
      top: 3px;
      left: 3px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
    }
  }

  ${On} {
    margin-left: 7px;
    font-size: var(--font-size-11);
  }

  ${Off} {
    margin-right: 6px;
    font-size: var(--font-size-11);
  }

  .inventory-row-selected {
    background: var(--color-primary-soft) !important;
    box-shadow: inset 3px 0 0 var(--color-primary);
  }

  @media (max-width: 720px) {
    height: auto;
    min-height: 584px;
  }
`;

const InventoryDetailCard = styled.section`
  min-width: 0;
  height: 584px;
  padding: 14px;
  overflow-y: auto;
  border: 1px solid #d9e2ec;
  border-radius: 9px;
  background: var(--color-surface);

  @media (max-width: 960px) {
    height: auto;
  }
`;

const DetailHead = styled.div`
  display: flex;
  min-height: 52px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e9ee;
`;

const SelectedInventoryLabel = styled.span`
  display: block;
  margin-bottom: 5px;
  color: var(--color-primary-strong);
  font-size: var(--font-size-12);
  font-weight: 750;
`;

const DetailTitle = styled.strong`
  display: block;
  max-width: 150px;
  overflow: hidden;
  color: #202a35;
  font-size: var(--font-size-15);
  font-weight: 750;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PublishBadge = styled.span`
  display: inline-flex;
  height: 23px;
  flex: 0 0 auto;
  align-items: center;
  padding: 0 8px;
  border-radius: 999px;
  background: ${({$active}) => ($active ? "#e8f7ef" : "#f1f3f5")};
  color: ${({$active}) => ($active ? "#178653" : "#77818c")};
  font-size: var(--font-size-12);
  font-weight: 750;
`;

const DetailSection = styled.div`
  padding: 14px 0 11px;
  border-bottom: 1px solid #e9edf1;
`;

const DetailSectionTitle = styled.div`
  margin-bottom: 10px;
  color: #2d3742;
  font-size: var(--font-size-14);
  font-weight: 750;
`;

const DetailRow = styled.div`
  display: flex;
  min-height: 24px;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: var(--color-text-muted);
  font-size: var(--font-size-13);

  strong {
    max-width: 135px;
    overflow: hidden;
    color: #45515d;
    font-size: var(--font-size-13);
    font-weight: 650;
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const CampaignDetailRow = styled(DetailRow)`
  min-height: 46px;
  margin: 8px 0;
  padding: 8px 10px;
  border: 1px solid #cfe0f4;
  border-radius: 8px;
  background: linear-gradient(135deg, #f4f8fd 0%, #eef5fc 100%);
  box-shadow: inset 3px 0 0 var(--color-primary-strong);

  > div {
    min-width: 58px;
  }
`;

const CampaignDetailLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #29445f;
  font-size: var(--font-size-11);
  font-weight: 750;

  i {
    width: 7px;
    height: 7px;
    flex: 0 0 7px;
    border-radius: 50%;
    background: var(--color-primary-strong);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, .12);
  }
`;

const DetailActionValue = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
  color: #45515d;
  font-size: var(--font-size-13);
  font-weight: 650;

  code {
    max-width: 100px;
    overflow: hidden;
    color: #45515d;
    font-family: inherit;
    font-size: var(--font-size-13);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  > span {
    color: var(--color-primary-strong);
    font-size: var(--font-size-13);
    font-weight: 700;
    text-decoration-color: var(--color-primary-focus) !important;
  }

  > div,
  > button {
    flex: 0 0 auto;
    cursor: pointer;
  }
`;

const SiteLink = styled.a`
  max-width: 135px;
  overflow: hidden;
  color: var(--color-primary-strong);
  font-size: var(--font-size-13);
  font-weight: 700;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: var(--color-primary-dark);
    text-decoration: underline;
  }
`;

const DetailButton = styled.button`
  width: 100%;
  height: 38px;
  margin-top: 16px;
  border: 1px solid var(--color-primary-strong);
  border-radius: 7px;
  background: var(--color-primary-strong);
  color: var(--color-surface);
  font-size: var(--font-size-13);
  font-weight: 750;
  cursor: pointer;

  &:hover {
    border-color: var(--color-primary-dark);
    background: var(--color-primary-dark);
  }
`;

const DetailEmpty = styled.div`
  display: flex;
  height: 100%;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 7px;
  color: #9aa3ad;
  font-size: var(--font-size-13);
  text-align: center;

  strong {
    color: #5f6b77;
    font-size: var(--font-size-14);
  }
`;

const DetailLoading = styled.div`
  display: flex;
  height: 100%;
  min-height: 260px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 9px;
  color: #6f7a86;
  font-size: var(--font-size-13);
  text-align: center;
`;

const PanelHeader = styled.div`
  display: flex;
  min-height: 66px;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
`;

const MediaTable = styled.div`
  overflow: hidden;
  border: 1px solid #e1e6eb;
  border-radius: 9px;
  background: var(--color-surface);
`;

const MediaTableHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 54px;
  min-height: 44px;
  align-items: center;
  border-bottom: 2px solid #8bb8ea;
  background: #f2f7fd;
  color: #34475a;
  font-size: var(--font-size-13);
  font-weight: 750;

  span {
    padding: 0 12px;
  }

  span:last-child {
    text-align: center;
  }
`;

const PanelTitle = styled.div`
  color: rgb(51, 51, 51);
  font-size: var(--font-size-15);
  font-weight: 700;
`;

const InventoryHeaderMain = styled.div`
  min-width: 0;
`;

const PanelTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PanelGuide = styled.div`
  max-width: 320px;
  margin-top: 4px;
  overflow: hidden;
  color: rgb(136, 136, 136);
  font-size: var(--font-size-12);
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectedMediaBadge = styled.div`
  display: flex;
  min-width: 0;
  max-width: 220px;
  height: 32px;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  border: 1px solid var(--color-primary-border);
  border-radius: 999px;
  background: var(--color-surface-muted);
  white-space: nowrap;

  span {
    color: #7b8794;
    font-size: var(--font-size-12);
    font-weight: 650;
  }

  strong {
    max-width: 86px;
    overflow: hidden;
    color: var(--color-primary-strong);
    font-size: var(--font-size-13);
    font-weight: 750;
    text-overflow: ellipsis;
  }

  em {
    max-width: 65px;
    overflow: hidden;
    color: var(--color-text-muted);
    font-size: var(--font-size-12);
    font-style: normal;
    text-overflow: ellipsis;
  }

  @media (max-width: 520px) {
    max-width: 150px;

    em {
      display: none;
    }
  }
`;

const PanelCount = styled.span`
  display: inline-flex;
  height: 24px;
  flex: 0 0 auto;
  align-items: center;
  padding: 0 9px;
  border: 1px solid var(--color-primary-border);
  border-radius: 999px;
  background: var(--color-primary-soft);
  color: var(--color-primary-strong);
  font-size: var(--font-size-13);
  font-weight: 700;
`;

const MediaListBody = styled.div`
  height: 470px;
  overflow-y: auto;
  background: var(--color-surface);

  @media (max-width: 720px) {
    height: auto;
    max-height: 260px;
  }
`;

const MediaListItem = styled.button`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 54px;
  width: 100%;
  min-height: 62px;
  align-items: center;
  gap: 0;
  padding: 0;
  border: 0;
  border-bottom: 1px solid #edf1f4;
  border-radius: 0;
  background: ${({$active}) => ($active ? "var(--color-primary-soft)" : "var(--color-surface)")};
  color: #36414d;
  text-align: left;
  cursor: pointer;
  transition: background .15s ease, box-shadow .15s ease;
  box-shadow: ${({$active}) => ($active ? "inset 3px 0 0 var(--color-primary)" : "none")};

  &:hover {
    background: ${({$active}) => ($active ? "var(--color-primary-soft)" : "var(--color-surface-muted)")};
  }
`;

const MediaIdentity = styled.span`
  min-width: 0;
  padding: 10px 12px;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: #26313d;
    font-size: var(--font-size-13);
    font-weight: 750;
  }

  span {
    margin-top: 4px;
    color: #929ba5;
    font-size: var(--font-size-13);
  }
`;

const MediaItemMeta = styled.span`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const InventoryCount = styled.span`
  display: inline-flex;
  min-width: 30px;
  height: 24px;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border-radius: 999px;
  background: #f1f4f7;
  color: #56616d;
  font-size: var(--font-size-13);
  font-weight: 700;
`;

const EmptyMedia = styled.div`
  padding: 36px 12px;
  color: #929ba5;
  font-size: var(--font-size-13);
  text-align: center;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  background: rgba(255, 255, 255, .78);
  color: #6f7a86;
  font-size: var(--font-size-13);
  backdrop-filter: blur(2px);
`;

const LoadingSpinner = styled.span`
  width: 18px;
  height: 18px;
  border: 2px solid var(--color-primary-muted);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: inventory-list-spin .7s linear infinite;

  @keyframes inventory-list-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
